import React, {useContext, useEffect, useState} from 'react';
import PdfStep from "./component/PdfStep";
import SignStep from "./component/SignStep";
import {useSearchParams} from "react-router-dom";
import {useMutation, useQuery} from "@tanstack/react-query";
import {getPdf, handleNextStep} from "./action/DigitalSignActions";
import {Button, Checkbox, FormControlLabel, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import {Box} from "@mui/system";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../util/component/CustomLoader";
import TextStep from "./component/TextStep";
import RaSignedDialog from "./component/RaSignedDialog";
import { IOptions } from '../types';

export interface IStep {
    "step-url"?: string;
    "step-key"?: string;
    options?: IOptions;
    rentalAgreementId?: string | null;
    customerId?: string | null;
    ra?: string | null;
    signature?: string | null;
    tenant?: string | null;
    dealer?: string | null;
    language?: string | null;
}

const DigitalSign = () => {
    //http://localhost:3000/external/network/digital-sign?code=XXX&username=YYYY&ra=00001&idRentalAgreement=525&cbId=2754&flgThirdParty=true&flgProfiledMarketing=true&flgMarketing=true&flgPrivacy=true
    const {t, i18n} = useTranslation(['network']);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState<IStep>({
        rentalAgreementId: searchParams.get('idRentalAgreement'),
        customerId: searchParams.get('cbId'),
        ra: searchParams.get('ra'),
        tenant: searchParams.get('tenant'),
        dealer: searchParams.get('dealer'),
        language: searchParams.get('language'),
        "step-url": "/init",
        "step-key": "init",
        signature: null,
        options: {
            promoDrivaliaConsent: searchParams.get('flgThirdParty') === 'true',
            promoDrivaliaConsentChannels: [],
            preferenciesConsent: searchParams.get('flgProfiledMarketing') === 'true',
            preferenciesConsentChannels: [],
            promoOthersConsent: searchParams.get('flgMarketing') === 'true',
            promoOthersConsentChannels: [],
            viewInfoAck: false, //searchParams.get('flgPrivacy') === 'true',
            viewInfoAckChannels: []
        }
    });
    const [queryPdfValues, setQueryPdfValues] = useState<any | null>(null);
    const [file, setFile] = useState<any | null>(null);
    const [isPdfReaded, setIsPdfReaded] = useState<boolean>(false);
    const [signRef, setSignRef] = useState<SignatureCanvas | null>(null);
    const [signIsPresent, setSignIsPresent] = useState(false);
    const [raSignIsClosed, setRaSignIsClosed] = useState(false);
    const width = window.innerWidth * 80 / 100;

    const handleClose = () => {
        setRaSignIsClosed(true);
    }

    const styles = {
        container: {
            margin: "16px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
        },
        content: {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
        },
        title: {
            marginBottom: "32px",
        },
    };

    const getStep = () => {
        switch (step["step-key"]) {
            case 'init':
            case 'pdf':
                return <PdfStep file={file} stepKey={step["step-key"]}/>;
            case 'CLS':
                return <>
                    <TextStep stepKey={step["step-key"]}/>
                    <SignStep hideTitle={true} signRef={signRef} setSignIsPresent={setSignIsPresent}
                              setSignRef={setSignRef}
                              step={step} setStep={setStep}/>
                </>
            case 'TAC':
            case 'PAC':
                return <>
                    <PdfStep file={file} stepKey={step["step-key"]}/>
                    <SignStep hideTitle={true} signRef={signRef} setSignIsPresent={setSignIsPresent}
                              setSignRef={setSignRef}
                              step={step} setStep={setStep}/>
                </>;
            case 'END':
            default:
                return <SignStep signRef={signRef} setSignIsPresent={setSignIsPresent} setSignRef={setSignRef}
                                 step={step} setStep={setStep}/>;
        }
    }
    const getHandler = () => {
        switch (step["step-key"]) {
            case 'init':
                return <Box sx={{width: width}}>
                    <Grid container mt={2}
                          direction="row"
                          justifyContent="center"
                          alignItems="center">
                        <Grid item>
                            <FormControlLabel
                                sx={{marginTop: "16px", marginBottom: "16px"}}
                                control={<Checkbox defaultChecked={false}/>}
                                label={t(`digitalSign.consent.init`)}
                                onChange={(e, checked) => setIsPdfReaded(checked)}/>
                        </Grid>
                        <Grid item>
                            <Button onClick={() => handleNextStepMutation.mutate(step)}
                                    disabled={!isPdfReaded}>{t('digitalSign.label.confirm')}</Button>
                        </Grid>
                    </Grid>
                </Box>;
            case 'pdf':
                return <Box sx={{width: width}}>
                    <Grid container mt={2}
                          direction="row"
                          justifyContent="center"
                          alignItems="center">
                        <Grid item>
                            <Button onClick={handleClose}>{t('digitalSign.label.close')}</Button>
                        </Grid>
                    </Grid>
                </Box>;
            case 'PAC':
                return <Box sx={{width: width}}>
                    <Grid container mt={2}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center">
                        <Grid item>
                            <Button variant="outlined" onClick={() => {
                                signRef?.clear();
                                setSignIsPresent(false);
                            }}>{t('digitalSign.label.clear')}</Button>
                        </Grid>
                        <Grid item>
                            <Button onClick={() => {
                                let stepTmp = step;
                                stepTmp.signature = signRef?.getSignaturePad().toDataURL('image/png') || null;
                                handleNextStepMutation.mutate(stepTmp);
                            }}
                                    disabled={!signIsPresent || !step.options?.viewInfoAck}>{t('digitalSign.label.confirm')}</Button>
                        </Grid>
                    </Grid>
                </Box>;
            default:
                return <Box sx={{width: width}}>
                    <Grid container mt={2}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center">
                        <Grid item>
                            <Button variant="outlined" onClick={() => {
                                signRef?.clear();
                                setSignIsPresent(false);
                            }}>{t('digitalSign.label.clear')}</Button>
                        </Grid>
                        <Grid item>
                            <Button onClick={() => {
                                let stepTmp = step;
                                stepTmp.signature = signRef?.getSignaturePad().toDataURL('image/png') || null;
                                handleNextStepMutation.mutate(stepTmp);
                            }} disabled={!signIsPresent}>{t('digitalSign.label.confirm')}</Button>
                        </Grid>
                    </Grid>
                </Box>;
        }
    }

    const getPdfQuery = useQuery(['getPdf', {queryPdfValues}], getPdf, {
        enabled: !!queryPdfValues,
        onSuccess: (data: any) => {
            setFile(data.data);
        },
        onError: (error: any) => {
            console.log(error);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('digitalSign.message.error.tryLater'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    })

    const handleNextStepMutation = useMutation({
        mutationFn: (params: any) => {
            return handleNextStep(params)
        },
        onSuccess: (data: any) => {
            setStep({
                ...step,
                ...data.data,
                "step-key": step["step-key"] === 'END' ? 'pdf' : data.data["step-key"]
            });
            setFile(null);
            setSignIsPresent(false);
            signRef?.clear();
        },
        onError: (error: any) => {
           /* switch (step["step-key"]) {
                case 'init':
                    setStep({
                        ...step,
                        "step-url": "/step",
                        "step-key": "TAC",
                    });
                    break;
                case 'TAC':
                    setStep({
                        ...step,
                        "step-url": "/step",
                        "step-key": "CLS",
                    });
                    break;
                case 'CLS':
                    setStep({
                        ...step,
                        "step-url": "/consent",
                        "step-key": "PAC",
                    });
                    break;
                case 'PAC':
                    setStep({
                        ...step,
                        "step-url": "/step",
                        "step-key": "END",
                    });
                    break;
                case 'END':
                    setStep({
                        ...step,
                        "step-url": "/step",
                        "step-key": "pdf",
                    });
                    break;
            }
            setFile(null);
            setSignIsPresent(false);
            signRef?.clear();*/
            console.log(error);
            if (error.response.status === 409) {
                let errorDetails = JSON.parse(error.response.data.errorDetails);
                if (errorDetails?.error === 'WORKFLOW_COMPLETED') {
                    setStep({
                        ...step,
                        "step-key": 'pdf'
                    });
                    document.body.scrollTop = 0;
                    document.documentElement.scrollTop = 0;
                }
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('digitalSign.message.error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
        }
    });

    useEffect(() => {
        let languagevar = searchParams.get('language')?.trim() || i18n.language.split('-')[0] || "en";
        if ((!queryPdfValues || queryPdfValues.stepKey !== step["step-key"] || queryPdfValues.language !== i18n.language.split('-')[0]) && (step["step-key"] !== 'CLS')) {
            setQueryPdfValues({idRa: searchParams.get('ra'), language: languagevar, stepKey: step["step-key"], tenant: searchParams.get('tenant')});
        }
    }, [step["step-key"], i18n.language]);

    return (
        <>
            {!raSignIsClosed ?
                <Box sx={styles.container}>
                    <Box sx={styles.content}>
                        {getStep()}
                    </Box>
                    <Box sx={styles.content}>
                        {getHandler()}
                    </Box>
                    <CustomLoader loading={getPdfQuery.isFetching || handleNextStepMutation.isLoading}/>
                </Box>
                : <RaSignedDialog/>}
        </>
    );
};

export default DigitalSign;